import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../ai-omnibar/ai-capability-registry";

/**
 * What the CICO reports page can do for the AI omnibar.
 *
 * Its issue toggles (missed clock-in, missed clock-out, has notes, unassigned) are deliberately left
 * out: each is a separate boolean the page combines in ways the words "visit issues" do not settle,
 * and guessing which ones a request meant would quietly answer a different question.
 */
declareAiCapability({
  id: "cicoIssues.filter",
  title: "Filter the CICO reports list",
  effect: "prefill",
  description:
    "Sets the filters on Reports > CICO Reports: the visit date range, office, contract, the agency member the issue is assigned to, and a free-text search over caregiver and patient names. The page opens on yesterday and today. Only works while that page is open.",
  location: "the filter bar of the Reports > CICO Reports page",
  scope: "page",
  permissionKey: "view_cico_issues_visits",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First visit day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last visit day to include, as yyyy-MM-dd.",
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these offices.",
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these contracts.",
    },
    {
      name: "assignedTo",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to issues assigned to these agency members.",
    },
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text matched against caregiver and patient names.",
    },
  ],
  examples: [
    "cico issues last week",
    "cico reports for the europe office",
    "cico issues assigned to smith",
  ],
});

function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("cicoIssues.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

export function useCicoIssuesAiCapabilities(params: {
  /** The page's own setValue, typed loosely: this hook only sets keys it declares. */
  setValue: (key: string, value: unknown) => void;
}): void {
  const { setValue } = params;
  const { queries } = useApi();

  const setValueRef = React.useRef(setValue);
  setValueRef.current = setValue;

  const offices = useQuery(queries.office.list());
  const contracts = useQuery(queries.contract.list());
  const agencyMembers = useQuery(queries.agencyMember.list());

  useContributedOptions("office", offices.data?.offices);
  useContributedOptions("contract", contracts.data?.contractTypes);
  useContributedOptions(
    "assignedTo",
    React.useMemo(
      () =>
        (agencyMembers.data?.agencyMembers ?? []).map((member) => ({
          id: member.id,
          name: [member.firstName, member.lastName].filter(Boolean).join(" "),
        })),
      [agencyMembers.data?.agencyMembers]
    )
  );

  // Not available until the lists have arrived: an empty option list turns a real filter into a
  // free-text search, so "europe" would land in the search box instead of the office filter.
  const listsLoaded = offices.isSuccess && contracts.isSuccess && agencyMembers.isSuccess;

  useProvideAiCapability(
    "cicoIssues.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.from !== undefined) {
        setValueRef.current("from", String(args.from));
      }

      if (args.to !== undefined) {
        setValueRef.current("to", String(args.to));
      }

      if (args.from !== undefined || args.to !== undefined) {
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      for (const [arg, key] of [
        ["office", "officeIds"],
        ["contract", "contractIds"],
        ["assignedTo", "assignedAgencyMemberIds"],
      ] as const) {
        if (args[arg] === undefined) {
          continue;
        }

        setValueRef.current(
          key,
          asList(args[arg]).map((one) => Number(one))
        );
        applied.push(label(arg));
      }

      if (args.search !== undefined) {
        setValueRef.current("freeText", String(args.search));
        applied.push(`"${String(args.search)}"`);
      }

      return applied.length === 0
        ? "Re-applied the CICO filters"
        : `Showing CICO issues: ${applied.join(", ")}`;
    },
    { enabled: listsLoaded }
  );
}
